import type { CSSProperties } from 'react';

import { Check } from '@phosphor-icons/react';
import classNames from 'classnames';
import { MotionConfig, motion } from 'motion/react';

import { getContrastRatio } from '@/utils/colorContrast';

import { useAnchorable } from '../../hooks/useAnchorable';

import {
  TIP_JAR_BLOCK_COLOR_KEYS,
  TIP_JAR_DEFAULT_COLORS,
  TIP_JAR_LEGACY_DEFAULT_COLORS,
  type TipJarColorAttribute,
} from './TipJar.constants';
import * as styles from './TipJar.css';
import type { TipJarBlockColors, TPostTipJarElement, TPostTipJarProps } from './TipJar.types';

const COLOR_ATTRIBUTES = Object.keys(TIP_JAR_DEFAULT_COLORS) as TipJarColorAttribute[];
// Untouched blocks were saved before Text on button existed, so only these tell them apart.
const LEGACY_COLOR_ATTRIBUTES = Object.keys(TIP_JAR_LEGACY_DEFAULT_COLORS) as TipJarColorAttribute[];
// The email takes six-digit hex only, so the web block does too, and the two never disagree.
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const matchesColors = (attrs: TPostTipJarElement['attrs'], colors: Partial<Record<TipJarColorAttribute, string>>) =>
  LEGACY_COLOR_ATTRIBUTES.every((attribute) => attrs[attribute]?.toUpperCase() === colors[attribute]);

// Mirrors ColorContrastMethods#contrast_color_for in Swarm: white or #222222, whichever stands out more.
const contrastingTextColor = (hex: string) => {
  const whiteContrast = getContrastRatio(hex, '#FFFFFF') ?? 0;
  const darkContrast = getContrastRatio(hex, '#222222') ?? 0;

  return whiteContrast >= darkContrast ? '#FFFFFF' : '#222222';
};

type WriterColors = Partial<Record<TipJarColorAttribute, string>>;

const hexColors = (colorFor: (attribute: TipJarColorAttribute) => string | undefined): WriterColors =>
  Object.fromEntries(
    COLOR_ATTRIBUTES.flatMap((attribute) => {
      const value = colorFor(attribute);

      return value && HEX_COLOR.test(value) ? [[attribute, value]] : [];
    }),
  );

// The post's colors over the jar's defaults. An exact default palette counts as unset: the writer never picked those
// colors for this post, so the jar's defaults or the theme decide them.
const readWriterColors = (attrs: TPostTipJarElement['attrs']): WriterColors => {
  const jarColors: TipJarBlockColors = attrs.data?.colors ?? {};
  const defaults = hexColors((attribute) => jarColors[TIP_JAR_BLOCK_COLOR_KEYS[attribute]]);
  const untouched = matchesColors(attrs, TIP_JAR_LEGACY_DEFAULT_COLORS) || matchesColors(attrs, TIP_JAR_DEFAULT_COLORS);

  return {
    ...defaults,
    ...hexColors((attribute) =>
      untouched && LEGACY_COLOR_ATTRIBUTES.includes(attribute) ? undefined : attrs[attribute],
    ),
  };
};

const colorVariables = ({ backgroundColor, textColor, progressColor, onProgressColor }: WriterColors) =>
  ({
    '--tip-jar-background': backgroundColor,
    // A background without a text color would leave the post's text on it, which may not show.
    '--tip-jar-text': textColor ?? (backgroundColor && contrastingTextColor(backgroundColor)),
    '--tip-jar-progress': progressColor,
    '--tip-jar-on-progress': onProgressColor ?? (progressColor && contrastingTextColor(progressColor)),
  }) as CSSProperties;

// Children sit under the goal bar, where the post editor says emails leave the goal out.
export const PostTipJar = ({ element, isLive, isIndexingDisabled, children }: TPostTipJarProps) => {
  const { attrs } = element;
  const { anchorAttributes } = useAnchorable(attrs);

  if (!attrs.data) {
    return null;
  }

  const { data } = attrs;
  const writerColors = readWriterColors(attrs);
  const fillScale = (data.percent_funded ?? 0) / 100;
  const linkRel = isIndexingDisabled ? 'nofollow noopener' : undefined;

  return (
    <MotionConfig reducedMotion="user">
      <div {...anchorAttributes} className={styles.container}>
        <div
          className={classNames(styles.block, writerColors.backgroundColor && styles.card)}
          style={colorVariables(writerColors)}
        >
          {/* Divs, not p/ul/li: the post editor's own paragraph and list rules would override the block's spacing. */}
          <div className={styles.heading}>{attrs.heading || data.labels.heading}</div>
          <div className={styles.subtitle}>{attrs.body || data.labels.subtitle}</div>

          {data.has_goal && (
            <div className={styles.progress}>
              <div className={styles.track} aria-hidden>
                {/* Fills on scroll into view on the live site; the editor shows where it lands. */}
                <motion.div
                  className={styles.fill}
                  initial={{ scaleX: isLive ? 0 : fillScale }}
                  whileInView={{ scaleX: fillScale }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>

              {data.labels.summary && <div className={styles.summary}>{data.labels.summary}</div>}

              {data.milestones.length > 0 && (
                // biome-ignore lint/a11y/useSemanticElements: ul/li pick up the post editor's list spacing
                <div role="list" className={styles.milestones}>
                  {data.milestones.map((milestone) => (
                    // biome-ignore lint/a11y/useSemanticElements: see the list above
                    <div
                      role="listitem"
                      key={`${milestone.label}-${milestone.amount}`}
                      className={classNames(styles.milestone, milestone.reached && styles.milestoneReached)}
                    >
                      {milestone.reached && (
                        <Check className={styles.milestoneCheck} size={12} weight="bold" aria-hidden />
                      )}
                      {[milestone.amount, milestone.label].filter(Boolean).join(' ')}
                    </div>
                  ))}
                </div>
              )}

              {children}
            </div>
          )}

          <div className={styles.buttons}>
            {data.amount_buttons.map((button) => (
              <a key={button.url} href={button.url} rel={linkRel} className={styles.button}>
                {button.label}
              </a>
            ))}
          </div>

          <a href={data.page_url} rel={linkRel} className={styles.otherAmount}>
            {data.labels.other_amount}
          </a>

          {data.labels.supporters && <div className={styles.supporters}>{data.labels.supporters}</div>}
        </div>
      </div>
    </MotionConfig>
  );
};
